import type { AABB, PolygonWithHoles, Ring, Vec2 } from '../geo/types';
import type { Layout } from '../data/types';
import type { PolygonIndex } from '../geo/polygonIndex';
import type { BridgeDeck } from './types';
import { segmentIntersection } from '../geo/polygon';
import { fromRing, inflate, intersection } from '../geo/clip';
import { nieuweBrugWestHead } from './nieuweBrugWestHead';

const MIN_BRIDGE_LENGTH_M = 0.01;
// Surveyed bridge ends stop up to 5.6 m short of the banks the channel strips produce, and a bank
// that meets a bridge at an angle leaves a wedge of water beside an end that is dry on its axis.
// Each end is carried to the farthest bank found across the deck's width within this reach, and a
// little past it, before the span is clipped back to the water.
const LANDING_SEARCH_M = 20;
const LANDING_OVERLAP_M = 0.5;
const LANDING_RAY_SPACING_M = 0.5;
// Decks are clipped to the water, so their bank edges sit within Clipper's 1 mm rounding of the
// shore; taken as they are, they leave sub-millimetre wet seams between deck and quay.
const DECK_SEAM_M = 0.01;

function along(p: Vec2, dir: Vec2, distance: number): Vec2 {
  return [p[0] + dir[0] * distance, p[1] + dir[1] * distance];
}

function spanRect(start: Vec2, end: Vec2, dir: Vec2, halfWidth: number): PolygonWithHoles {
  const n: Vec2 = [-dir[1] * halfWidth, dir[0] * halfWidth];
  const ring: Ring = [
    [start[0] + n[0], start[1] + n[1]],
    [end[0] + n[0], end[1] + n[1]],
    [end[0] - n[0], end[1] - n[1]],
    [start[0] - n[0], start[1] - n[1]],
  ];
  return fromRing(ring);
}

function bankDistance(from: Vec2, outward: Vec2, waterIndex: PolygonIndex): number {
  if (!waterIndex.contains(from)) return 0;
  const far = along(from, outward, LANDING_SEARCH_M);
  const box: AABB = {
    minX: Math.min(from[0], far[0]),
    minY: Math.min(from[1], far[1]),
    maxX: Math.max(from[0], far[0]),
    maxY: Math.max(from[1], far[1]),
  };
  const nearest = waterIndex.edgesNear(box).reduce((best, edge) => {
    const hit = segmentIntersection({ a: from, b: far }, edge);
    return hit ? Math.min(best, hit.tS) : best;
  }, Infinity);
  return Number.isFinite(nearest) ? nearest * LANDING_SEARCH_M : 0;
}

function landingReach(end: Vec2, outward: Vec2, halfWidth: number, index: PolygonIndex): number {
  const rays = Math.max(2, Math.ceil((2 * halfWidth) / LANDING_RAY_SPACING_M)) + 1;
  const across: Vec2 = [-outward[1], outward[0]];
  const reach = Array.from({ length: rays }, (_, i) =>
    bankDistance(along(end, across, -halfWidth + (2 * halfWidth * i) / (rays - 1)), outward, index)
  ).reduce((a, b) => Math.max(a, b), 0);
  return reach > 0 ? reach + LANDING_OVERLAP_M : 0;
}

// PLAN §5 step 7, landed on both banks. Only water the surveyed span touches becomes deck, so a
// landing that reaches past a narrow quay never lays a deck on the next canal.
export function buildBridges(
  layout: Layout,
  water: readonly PolygonWithHoles[],
  waterIndex: PolygonIndex
): readonly BridgeDeck[] {
  return layout.bridges.flatMap((bridge) => {
    const a = bridge.a as Vec2;
    const b = bridge.b as Vec2;
    const len = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2);
    if (len < MIN_BRIDGE_LENGTH_M) return [];

    const dir: Vec2 = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
    const halfWidth = bridge.width / 2;
    const back = landingReach(a, [-dir[0], -dir[1]], halfWidth, waterIndex);
    const ahead = landingReach(b, dir, halfWidth, waterIndex);
    const landed = spanRect(along(a, dir, -back), along(b, dir, ahead), dir, halfWidth);
    const surveyed = spanRect(a, b, dir, halfWidth);
    const deck = intersection([landed], water).filter(
      (piece) => intersection([piece], [surveyed]).length > 0
    );

    const gate = bridge.gate === undefined ? {} : { gate: bridge.gate };
    const head = bridge.id === 'bridge-nieuwe-brug' ? nieuweBrugWestHead(layout) : null;
    const landing = head ? { westLandingEnd: head.bridgeFlatEnd, westStairRun: head.stairRun } : {};
    return [
      { id: bridge.id, kind: bridge.kind, a, b, width: bridge.width, deck, ...gate, ...landing },
    ];
  });
}

// Where a walker stands on a deck rather than in the water under it.
export function deckFootprint(bridges: readonly BridgeDeck[]): readonly PolygonWithHoles[] {
  const decks = bridges.flatMap((bridge) => bridge.deck);
  return decks.length > 0 ? inflate(decks, DECK_SEAM_M, 'miter') : [];
}
